import { useEffect, useState } from "react";
import { useLocale } from "@/lib/i18n";
import {
  PRIVACY_NOTICE_KEY,
  PRIVACY_SETTINGS_EVENT,
  hasAdsConsent,
  hasAnalyticsConsent,
  hasGlobalPrivacyControl,
  hydrateConsent,
  readConsent,
  writeConsent,
} from "@/lib/consent";

const COPY = {
  en: {
    notice:
      "We use analytics to understand site performance and advertising measurement to understand campaign effectiveness. You can change these choices at any time.",
    choices: "Privacy choices",
    dismiss: "Got it",
    title: "Privacy choices",
    intro: "Choose which optional measurement technologies Shugert may use on this site.",
    necessary: "Necessary",
    necessaryHelp: "Required for core site functionality and security. Always on.",
    analytics: "Analytics",
    analyticsHelp: "Helps us understand visits, navigation, attribution, and site performance using Google Analytics.",
    ads: "Advertising measurement",
    adsHelp: "Helps us measure campaign performance, including ChatGPT Ads conversion attribution.",
    gpc: "Disabled because your browser is sending Global Privacy Control.",
    save: "Save preferences",
    close: "Close",
    learn: "Privacy policy",
    learnHref: "/privacy",
  },
  es: {
    notice:
      "Usamos analítica para entender el rendimiento del sitio y medición publicitaria para evaluar nuestras campañas. Puedes cambiar estas preferencias en cualquier momento.",
    choices: "Opciones de privacidad",
    dismiss: "Entendido",
    title: "Opciones de privacidad",
    intro: "Elige qué tecnologías de medición opcionales puede utilizar Shugert en este sitio.",
    necessary: "Necesarias",
    necessaryHelp: "Requeridas para el funcionamiento y la seguridad del sitio. Siempre activas.",
    analytics: "Analítica",
    analyticsHelp: "Nos ayuda a entender visitas, navegación, atribución y rendimiento mediante Google Analytics.",
    ads: "Medición publicitaria",
    adsHelp: "Nos ayuda a medir el rendimiento de campañas, incluida la atribución de conversiones de ChatGPT Ads.",
    gpc: "Desactivada porque tu navegador está enviando Global Privacy Control.",
    save: "Guardar preferencias",
    close: "Cerrar",
    learn: "Política de privacidad",
    learnHref: "/es/privacidad",
  },
} as const;

function effectivePreferences() {
  const current = readConsent();
  if (current) return current;
  return {
    analytics: hasAnalyticsConsent(),
    ads: hasAdsConsent(),
    ts: 0,
  };
}

export function CookieBanner() {
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(true);
  const gpc = hasGlobalPrivacyControl();
  const t = COPY[locale];

  useEffect(() => {
    hydrateConsent();

    const sync = () => {
      const prefs = effectivePreferences();
      setAnalytics(prefs.analytics);
      setAds(gpc ? false : prefs.ads);
    };

    const openSettings = () => {
      sync();
      setManaging(true);
      setOpen(true);
    };

    sync();
    window.addEventListener(PRIVACY_SETTINGS_EVENT, openSettings);

    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(PRIVACY_NOTICE_KEY) === "1";
    } catch {
      /* storage unavailable */
    }

    // This is a non-blocking notice, not an opt-in gate. Existing explicit
    // preferences remain authoritative and are never overwritten here.
    const timer = !readConsent() && !dismissed
      ? window.setTimeout(() => setOpen(true), 1200)
      : undefined;

    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener(PRIVACY_SETTINGS_EVENT, openSettings);
    };
  }, [gpc]);

  const rememberNotice = () => {
    try {
      window.localStorage.setItem(PRIVACY_NOTICE_KEY, "1");
    } catch {
      /* storage unavailable */
    }
  };

  const dismiss = () => {
    rememberNotice();
    setOpen(false);
    setManaging(false);
  };

  const save = () => {
    writeConsent({ analytics, ads: gpc ? false : ads });
    rememberNotice();
    setOpen(false);
    setManaging(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.title}
      className="fixed inset-x-3 bottom-20 z-[60] mx-auto max-w-3xl rounded-2xl border border-border/60 bg-background/95 p-4 shadow-lg backdrop-blur md:inset-x-auto md:bottom-4 md:right-auto md:left-4 md:w-[30rem] md:p-5"
    >
      {!managing ? (
        <>
          <p className="text-xs leading-relaxed text-muted-foreground md:text-sm">
            {t.notice}{" "}
            <a href={t.learnHref} className="underline underline-offset-2 hover:text-foreground">
              {t.learn}
            </a>
            .
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setManaging(true)}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted md:text-sm"
            >
              {t.choices}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-full bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-colors hover:bg-foreground/90 md:text-sm"
            >
              {t.dismiss}
            </button>
          </div>
        </>
      ) : (
        <>
          <h2 className="text-base font-semibold text-foreground">{t.title}</h2>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground md:text-sm">{t.intro}</p>

          <div className="mt-4 space-y-3">
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{t.necessary}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t.necessaryHelp}</p>
              </div>
              <input type="checkbox" checked readOnly disabled aria-label={t.necessary} className="mt-1 h-4 w-4" />
            </div>

            <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-border/60 p-3">
              <span>
                <span className="block text-sm font-medium text-foreground">{t.analytics}</span>
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{t.analyticsHelp}</span>
              </span>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
                aria-label={t.analytics}
                className="mt-1 h-4 w-4"
              />
            </label>

            <label className={`flex items-start justify-between gap-4 rounded-xl border border-border/60 p-3 ${gpc ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
              <span>
                <span className="block text-sm font-medium text-foreground">{t.ads}</span>
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{t.adsHelp}</span>
                {gpc ? <span className="mt-1 block text-xs font-medium text-foreground">{t.gpc}</span> : null}
              </span>
              <input
                type="checkbox"
                checked={gpc ? false : ads}
                onChange={(event) => setAds(event.target.checked)}
                disabled={gpc}
                aria-label={t.ads}
                className="mt-1 h-4 w-4"
              />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <a href={t.learnHref} className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground">
              {t.learn}
            </a>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setManaging(false);
                  setOpen(false);
                }}
                className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted md:text-sm"
              >
                {t.close}
              </button>
              <button
                type="button"
                onClick={save}
                className="rounded-full bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-colors hover:bg-foreground/90 md:text-sm"
              >
                {t.save}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CookieBanner;
